import type { TravellerWithCollectionMembershipsDTO } from '@/api/trafikportalen/models';

export interface DuplicateSearchFields {
  email?: string;
  first_name?: string;
  last_name?: string;
  national_identity_number?: string;
  phone?: string;
}

export interface DuplicateMatch {
  traveller: TravellerWithCollectionMembershipsDTO;
  matchedFields: string[];
}

const PREFIX_FIELDS = ['email', 'first_name', 'last_name'] as const;

const isPrefixField = (
  fieldName: string,
): fieldName is (typeof PREFIX_FIELDS)[number] =>
  PREFIX_FIELDS.includes(fieldName as (typeof PREFIX_FIELDS)[number]);

const isFieldMatch = (
  fieldName: string,
  trimmedValue: string,
  traveller: TravellerWithCollectionMembershipsDTO['traveller'],
): boolean => {
  switch (fieldName) {
    case 'email':
      return (
        traveller.email?.toLowerCase().startsWith(trimmedValue.toLowerCase()) ??
        false
      );
    case 'first_name':
      return (
        traveller.first_name
          ?.toLowerCase()
          .startsWith(trimmedValue.toLowerCase()) ?? false
      );
    case 'last_name':
      return (
        traveller.last_name
          ?.toLowerCase()
          .startsWith(trimmedValue.toLowerCase()) ?? false
      );
    case 'national_identity_number':
      return traveller.national_identity_number === trimmedValue;
    case 'phone':
      return traveller.phone === trimmedValue;
    default:
      return false;
  }
};

export const findTravellerDuplicates = (
  travellers: TravellerWithCollectionMembershipsDTO[] | undefined,
  searchFields: DuplicateSearchFields,
): DuplicateMatch[] => {
  if (!travellers?.length) {
    return [];
  }

  const activeTravellers = travellers.filter((item) =>
    item.memberships.some(
      (membership) =>
        membership.active && membership.collection_type === 'master',
    ),
  );

  const matches = new Map<string, DuplicateMatch>();

  Object.entries(searchFields).forEach(([fieldName, fieldValue]) => {
    if (
      !fieldValue ||
      typeof fieldValue !== 'string' ||
      fieldValue.trim() === ''
    ) {
      return;
    }

    const trimmedValue = fieldValue.trim();

    if (isPrefixField(fieldName) && trimmedValue.length < 3) {
      return;
    }

    activeTravellers.forEach((travellerData) => {
      if (!isFieldMatch(fieldName, trimmedValue, travellerData.traveller)) {
        return;
      }

      const travellerId = travellerData.traveller.id;
      const existingMatch = matches.get(travellerId);

      if (existingMatch) {
        existingMatch.matchedFields = [
          ...new Set([...existingMatch.matchedFields, fieldName]),
        ];
        return;
      }

      matches.set(travellerId, {
        traveller: travellerData,
        matchedFields: [fieldName],
      });
    });
  });

  return Array.from(matches.values())
    .sort((a, b) => b.matchedFields.length - a.matchedFields.length)
    .slice(0, 3);
};
